import { stopPollingMessages } from './stopPollingMessages';
import {
  setChatbotHasPollAnswers,
  setChatbotRowVersion,
  setChatbotUnreadMessagesCount,
  setChatbotPeriodicity,
  setChatbotPollingIntervalId,
  setChatbotIsTyping,
  setChatbotActionButtons,
  setChatbotMenuActionButtons,
  setChatbotMenuText,
  setChatbotMessageInfoBlock,
  setChatbotOnboardingButtons,
} from '../../actions/chatbot/actions';
import { openFeedbackChatbotModal } from '../../actions/feedbackChatbotModal';
import { setMessagesAsRead, setMessagesFulfilled } from '../../actions/messages';
import { setDeleteMessage } from '../../actions/sendMessage';
import { CHATBOT_ID } from '../../constants/chatbotId';
import { fetchChatbotMessages } from '../../services/сhatbot/fetchChatbotMessages';
import { fetchChatbotReadMessages } from '../../services/сhatbot/fetchChatbotReadMessages';
import { TThunkAction } from '../../types/redux/thunk';
import { findLastMessage } from '../../utils/findLastMessage';
import { performPollingWithRetry } from '../../utils/performPollingWithRetry/performPollingWithRetry';

type TStartPollingMessagesParams = {
  messageIdToDelete?: string;
  isQuick?: boolean;
  isMedium?: boolean;
} | void;

export const startPollingMessages =
  ({ messageIdToDelete, isQuick = false, isMedium = false }: TStartPollingMessagesParams = {}): TThunkAction<void> =>
  async (dispatch, getState, context) => {
    dispatch(stopPollingMessages()); // Останавливаем текущий поллинг перед запуском нового

    const showRating = context.config.get<boolean>('frontend-chats.showRating.Enabled') || false; // тогл для включения попапа с оценкой

    const quickPollingCountMax = context.config.get<number>('frontend-chats.chatbotQuickPollingCountMax') || 12; // Максимальное количество попыток быстрого поллинга

    const pollingPeriod = context.config.get<number>('frontend-chats.chatbotPollingPeriod') || 5000; // Период медленного поллинга
    const mediumPollingPeriod = context.config.get<number>('frontend-chats.chatbotMediumPollingPeriod') || 60000; // Период среднего поллинга
    const quickPollingPeriod = context.config.get<number>('frontend-chats.chatbotQuickPollingPeriod') || 5000; // Период быстрого поллинга

    const currentPollingPeriod = isQuick ? quickPollingPeriod : isMedium ? mediumPollingPeriod : pollingPeriod; // Основная функция поллинга, выполняющая запросы к серверу и обрабатывающая ответы

    // Функция поллинга, которая вызывается периодически
    const pollingFunction = async () => {
      const {
        chatbot: { rowVersion },
        chatWindow: { currentOpenedChatId },
      } = getState();

      // Проверка перешел ли пользователь в другой чат, и если да, то переключение на медленный поллинг
      if (currentOpenedChatId !== CHATBOT_ID && isMedium) {
        dispatch(startPollingMessages());

        return true;
      }

      // Выполняем запрос на получение сообщений от чат-бота
      const response = await fetchChatbotMessages(context, rowVersion);

      if (!response) {
        return false; // Если нет ответа, то считаем поллинг неуспешным
      }

      dispatch(setChatbotMenuText(response.menuText || ''));
      dispatch(setChatbotMessageInfoBlock(response.messageInfoBlock ?? undefined));
      dispatch(setChatbotActionButtons(response.actionButtons));
      dispatch(setChatbotMenuActionButtons(response.menuActionButtons));
      dispatch(setChatbotOnboardingButtons(response.onboardingButtons || []));

      if (showRating && response.showRatingPopup && currentOpenedChatId === CHATBOT_ID) {
        dispatch(openFeedbackChatbotModal());
      }

      if (currentOpenedChatId === CHATBOT_ID && response.unreadMessagesCount) {
        fetchChatbotReadMessages(context); // Отмечаем сообщения как прочитанные, если открытый чат с ботом
      }

      // Если нужно, отменяем отправку сообщения
      if (messageIdToDelete) {
        dispatch(
          setDeleteMessage({
            chatId: CHATBOT_ID,
            messageId: messageIdToDelete,
            messagesMap: context.custom.messagesMap,
          }),
        );
      }

      const isChatbotTyping = !!response.isTyping; // Определяем, печатает ли бот
      dispatch(setChatbotIsTyping(isChatbotTyping));

      // Обновляем состояние сообщений в чате
      dispatch(
        setMessagesFulfilled({
          messages: response.messages,
          chatId: CHATBOT_ID,
          messagesMap: context.custom.messagesMap,
        }),
      );

      //Выбираем только сообщения от пользователя
      const messageIds = response.messages.filter(message => !message.fromChatBot).map(message => message.messageId);

      //Делаем все сообщения от пользователя прочитанными
      dispatch(setMessagesAsRead({ chatId: CHATBOT_ID, messageIds, messagesMap: context.custom.messagesMap }));

      const { openedChats } = getState();
      const chatbotChat = openedChats[CHATBOT_ID];
      const messagesLastIndex = chatbotChat.chatMessages.length ? chatbotChat.chatMessages.length - 1 : 0;

      const lastMessage = findLastMessage({
        chat: chatbotChat,
        messageIndex: messagesLastIndex,
        incoming: false,
      });

      dispatch(setChatbotRowVersion((lastMessage?.rowVersion as number) || 0));
      dispatch(setChatbotUnreadMessagesCount(currentOpenedChatId === CHATBOT_ID ? 0 : response.unreadMessagesCount));
      dispatch(setChatbotPeriodicity(response.periodicity));
      dispatch(setChatbotHasPollAnswers(response.hasPollAnswers));

      if ((response.messages.length === 1 && !response.messages[0].fromChatBot) || response.messages.length === 0) {
        return false; // Если пришло только отправленное сообщение, то считаем поллинг неуспешным
      }

      if (lastMessage?.fromChatBot && isQuick) {
        dispatch(startPollingMessages()); // Перезапускаем поллинг, если получили сообщение от бота

        return true;
      }

      return false;
    };

    const onPollingComplete = () => {
      dispatch(startPollingMessages({ isMedium: true })); // Переход на средний поллинг после завершения попыток быстрого поллинга
    };

    if (isQuick) {
      // Если выбран быстрый поллинг, выполняем его с повторными попытками
      await performPollingWithRetry(pollingFunction, quickPollingCountMax, quickPollingPeriod);
      onPollingComplete(); // Выполняем действия после завершения всех попыток
    } else {
      // Если выбран обычный или средний поллинг
      const intervalId = window.setInterval(pollingFunction, currentPollingPeriod); // Запуск интервала для последующего поллинга
      dispatch(setChatbotPollingIntervalId(intervalId)); // Сохранение идентификатора интервала
    }
  };
